import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api';

const AdminCreateOrderModal = ({ onClose, onSuccess, showMessage }) => {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [manualName, setManualName] = useState('');
    const [manualPhone, setManualPhone] = useState('');
    const [manualAddress, setManualAddress] = useState('');
    const [deliveryMethod, setDeliveryMethod] = useState('Home Delivery');
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState({ name: '', price: '', quantity: '1', packing: '' });
    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [useExistingUser, setUseExistingUser] = useState(true);
    const [successOrder, setSuccessOrder] = useState(null);

    // New billing fields
    const [discountAmount, setDiscountAmount] = useState(0);
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [promoCode, setPromoCode] = useState('');

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const data = await callApi('/admin/users');
                setUsers(data || []);
            } catch (e) {
                console.error('Failed to fetch users', e);
            }
        };
        fetchUsers();
    }, []);

    const selectedUser = users.find(u => u._id === selectedUserId);

    const handleAddItem = () => {
        if (!newItem.name.trim() || !newItem.quantity) {
            showMessage('Please fill in item name and quantity.', 'error');
            return;
        }
        setItems(prev => [...prev, {
            _id: Date.now().toString(),
            name: newItem.name.trim(),
            price: parseFloat(newItem.price) || 0,
            quantity: parseInt(newItem.quantity) || 1,
        }]);
        setNewItem({ name: '', price: '', quantity: '1' });
        setShowDropdown(false);
    };

    const handleProductSearch = async (searchTerm) => {
        setNewItem(p => ({ ...p, name: searchTerm }));
        if (!searchTerm || searchTerm.trim().length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }
        try {
            const response = await callApi(`/admin/products?limit=5&search=${encodeURIComponent(searchTerm)}`);
            if (response && response.products) {
                setSearchResults(response.products);
                setShowDropdown(true);
            }
        } catch (err) {
            console.error('Failed to search products', err);
        }
    };

    const selectProductResult = (productData) => {
        const packingStr = productData.packageVolume || productData.pack || '';
        const match = packingStr.match(/(\d+)/);
        const totalUnits = match ? parseInt(match[1], 10) : 1;
        const mrp = productData.price || productData.mrp || 0;

        setNewItem({
            name: productData.name || productData.productName,
            price: totalUnits > 0 ? parseFloat((mrp / totalUnits).toFixed(2)) : mrp,
            quantity: totalUnits.toString(),
            packing: packingStr
        });
        setShowDropdown(false);
    };

    const handleRemoveItem = (id) => {
        setItems(prev => prev.filter(item => item._id !== id));
    };

    const subTotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const total = subTotal + (deliveryMethod === 'Home Delivery' ? Number(deliveryFee) : 0) - Number(discountAmount);

    const handleSubmit = async () => {
        const customerName = useExistingUser ? (selectedUser?.name || '') : manualName;
        const customerPhone = useExistingUser ? (selectedUser?.mobile || '') : manualPhone;
        const deliveryAddress = useExistingUser
            ? (selectedUser?.address || '')
            : manualAddress;

        if (!customerName || !customerPhone) {
            showMessage('Customer name and phone are required.', 'error');
            return;
        }
        if (deliveryMethod === 'Home Delivery' && !deliveryAddress) {
            showMessage('Delivery address is required for Home Delivery.', 'error');
            return;
        }
        if (items.length === 0) {
            showMessage('Please add at least one item.', 'error');
            return;
        }

        setIsSubmitting(true);

        let finalUserId = useExistingUser ? selectedUserId || null : null;

        if (!useExistingUser) {
            try {
                const cleanMobile = manualPhone.trim().replace(/\D/g, '').slice(-10);
                const alreadyExists = users.some(u => {
                    const uPhone = (u.mobile || '').replace(/\D/g, '').slice(-10);
                    return uPhone === cleanMobile;
                });

                if (!alreadyExists && cleanMobile.length === 10) {
                    const newUserRes = await callApi('/admin/users/create-customer', 'POST', {
                        name: manualName.trim(),
                        mobile: cleanMobile,
                        address: manualAddress ? manualAddress.trim() : ''
                    });
                    if (newUserRes && newUserRes.user && newUserRes.user._id) {
                        finalUserId = newUserRes.user._id;
                    }
                }
            } catch (custErr) {
                console.log("Customer save handled:", custErr);
            }
        }

        try {
            const response = await callApi('/orders', 'POST', {
                userId: finalUserId,
                items,
                subTotal,
                total,
                discountApplied: discountAmount,
                deliveryFee: deliveryMethod === 'Home Delivery' ? deliveryFee : 0,
                promoCode: promoCode,
                deliveryMethod,
                deliveryAddress: deliveryMethod === 'Home Delivery' ? deliveryAddress : undefined,
                customerName,
                customerPhone,
                isManualBill: true,
            });
            if (response.success || response._id || response.orderId) {
                showMessage('Order created successfully!', 'success');
                onSuccess();
                setSuccessOrder({
                    orderId: response.orderId || response._id,
                    total: total,
                    customerName: customerName
                });
            } else {
                showMessage(response.message || 'Failed to create order.', 'error');
            }
        } catch (error) {
            showMessage('Error creating order.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (successOrder) {
        const upiUrl = `upi://pay?pa=vimalaxmistore@ybl&pn=Pharmacy&am=${successOrder.total.toFixed(2)}&cu=INR`;
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(upiUrl)}`;

        return (
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden border border-slate-100 p-6 text-center animate-fade-in">
                    <div className="w-16 h-16 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                        <i className="fas fa-check"></i>
                    </div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2">Order Created!</h3>
                    <p className="text-slate-500 font-medium mb-6">Total Amount: <strong className="text-slate-800">₹{successOrder.total.toFixed(2)}</strong></p>
                    
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 mb-6 inline-block mx-auto">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Scan to Pay</p>
                        <img src={qrUrl} alt="UPI Payment QR" className="w-48 h-48 mx-auto rounded-xl shadow-sm" />
                        <p className="text-[10px] text-slate-400 mt-3 font-mono">vimalaxmistore@ybl</p>
                    </div>

                    <button onClick={onClose} className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-3 px-4 rounded-xl transition-colors">
                        Done
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
                {/* Header */}
                <div className="bg-slate-50 px-4 sm:px-8 py-4 sm:py-5 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                    <div>
                        <h3 className="text-xl font-extrabold text-slate-800">Create Manual Order</h3>
                        <p className="text-sm text-slate-500 mt-0.5">Create an order on behalf of a customer</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="overflow-y-auto flex-1 px-4 sm:px-8 py-4 sm:py-6 space-y-6">
                    {/* Customer Section */}
                    <div>
                        <h4 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                            <i className="fas fa-user text-sky-500"></i> Customer Details
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
                            <button
                                onClick={() => setUseExistingUser(true)}
                                className={`flex-1 py-2 rounded-lg font-bold text-sm border transition-colors ${useExistingUser ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                            >
                                <i className="fas fa-users mr-2"></i>Existing User
                            </button>
                            <button
                                onClick={() => setUseExistingUser(false)}
                                className={`flex-1 py-2 rounded-lg font-bold text-sm border transition-colors ${!useExistingUser ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                            >
                                <i className="fas fa-user-plus mr-2"></i>Walk-in / Manual
                            </button>
                        </div>

                        {useExistingUser ? (
                            <div className="space-y-3">
                                <select
                                    value={selectedUserId}
                                    onChange={e => setSelectedUserId(e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                >
                                    <option value="">-- Select a customer --</option>
                                    {users.filter(u => u.role === 'user').map(u => (
                                        <option key={u._id} value={u._id}>
                                            {u.name || 'Unnamed'} — {u.mobile}
                                        </option>
                                    ))}
                                </select>
                                {selectedUser && (
                                    <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 text-sm text-slate-600">
                                        <p><span className="font-bold">Name:</span> {selectedUser.name}</p>
                                        <p><span className="font-bold">Phone:</span> {selectedUser.mobile}</p>
                                        {selectedUser.address && <p><span className="font-bold">Address:</span> {selectedUser.address}</p>}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <input type="text" placeholder="Customer Name *" value={manualName} onChange={e => setManualName(e.target.value)} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500" />
                                <input type="text" placeholder="Phone Number *" value={manualPhone} onChange={e => setManualPhone(e.target.value)} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500" />
                            </div>
                        )}
                    </div>

                    {/* Delivery */}
                    <div>
                        <h4 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                            <i className="fas fa-truck text-emerald-500"></i> Delivery Method
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3">
                            {['Home Delivery', 'Store Pickup'].map(method => (
                                <button
                                    key={method}
                                    onClick={() => setDeliveryMethod(method)}
                                    className={`flex-1 py-2 rounded-lg font-bold text-sm border transition-colors ${deliveryMethod === method ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                                >
                                    {method}
                                </button>
                            ))}
                        </div>
                        {deliveryMethod === 'Home Delivery' && (
                            <textarea
                                placeholder="Delivery Address *"
                                value={useExistingUser ? (selectedUser?.address || manualAddress) : manualAddress}
                                onChange={e => setManualAddress(e.target.value)}
                                rows={2}
                                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                            />
                        )}
                    </div>

                    {/* Items Section */}
                    <div>
                        <h4 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                            <i className="fas fa-box text-violet-500"></i> Order Items
                        </h4>

                        <div className="flex flex-col sm:flex-row gap-2 mb-3 relative">
                            <div className="flex-1 relative">
                                <input type="text" placeholder="Item name" value={newItem.name} onChange={e => handleProductSearch(e.target.value)} onFocus={() => { if(searchResults.length > 0) setShowDropdown(true); }} onBlur={() => setTimeout(() => setShowDropdown(false), 200)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" autoComplete="off" />
                                {showDropdown && searchResults.length > 0 && (
                                    <ul className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                                        {searchResults.map((res, i) => (
                                            <li key={i} onMouseDown={() => selectProductResult(res)} className="px-3 py-2 hover:bg-sky-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                <div className="font-bold text-xs text-slate-800">{res.name || res.productName}</div>
                                                <div className="text-[10px] text-slate-500 flex justify-between">
                                                    <span>{res.packageVolume || res.pack || ''}</span>
                                                    <span className="text-sky-600 font-bold">₹{res.price || res.mrp}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <input type="number" placeholder="Per-Unit Price" value={newItem.price} onChange={e => setNewItem(p => ({ ...p, price: e.target.value }))} className="w-full sm:w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" title="Price per Unit" />
                                <input type="number" placeholder="Qty (Units)" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))} className="w-full sm:w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 font-bold text-sky-700" min="0.01" step="any" title="Quantity in Units" />
                            </div>
                            <button onClick={handleAddItem} className="w-full sm:w-auto bg-sky-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-sky-700 transition-colors">
                                <i className="fas fa-plus mr-2 sm:mr-0"></i><span className="sm:hidden">Add Item</span>
                            </button>
                        </div>

                        {items.length > 0 ? (
                            <div className="border border-slate-100 rounded-xl overflow-hidden">
                                {items.map((item, idx) => (
                                    <div key={item._id} className={`flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 text-sm gap-2 sm:gap-0 ${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                                        <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0">
                                            <span className="font-medium text-slate-700">{item.name}</span>
                                            <span className="text-slate-500 sm:mx-3 text-xs sm:text-sm">×{item.quantity}</span>
                                        </div>
                                        <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto">
                                            <span className="font-bold text-slate-800 w-auto sm:w-20 text-right">₹{(item.price * item.quantity).toFixed(2)}</span>
                                            <button onClick={() => handleRemoveItem(item._id)} className="ml-3 text-red-400 hover:text-red-600 transition-colors p-2 sm:p-0">
                                                <i className="fas fa-trash-alt text-sm sm:text-xs"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <div className="flex flex-col gap-2 px-4 py-3 bg-sky-50 border-t border-sky-100 font-extrabold text-slate-700">
                                    <div className="flex justify-between items-center text-sm font-bold border-b border-sky-200/50 pb-2">
                                        <span>Subtotal</span>
                                        <span>₹{subTotal.toFixed(2)}</span>
                                    </div>
                                    
                                    {deliveryMethod === 'Home Delivery' && (
                                        <div className="flex justify-between items-center text-sm font-bold border-b border-sky-200/50 py-2">
                                            <span>Delivery Fee</span>
                                            <div className="flex items-center gap-1">
                                                <span>₹</span>
                                                <input type="number" min="0" step="1" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} className="w-16 border border-sky-200 bg-white rounded px-2 py-1 text-right focus:outline-none focus:ring-2 focus:ring-sky-400" />
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-center text-sm font-bold border-b border-sky-200/50 py-2">
                                        <span className="text-emerald-600">Discount (-)</span>
                                        <div className="flex items-center gap-1 text-emerald-600">
                                            <span>₹</span>
                                            <input type="number" min="0" step="1" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} className="w-16 border border-emerald-200 bg-white rounded px-2 py-1 text-right focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center text-sm font-bold border-b border-sky-200/50 py-2">
                                        <span>Promo/Note</span>
                                        <input type="text" placeholder="e.g. WELCOME10" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className="w-28 border border-sky-200 bg-white rounded px-2 py-1 text-right uppercase focus:outline-none focus:ring-2 focus:ring-sky-400" />
                                    </div>

                                    <div className="flex justify-between items-center pt-2 mt-1 text-lg">
                                        <span>Grand Total</span>
                                        <span className="text-sky-700 text-xl">₹{total.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center text-slate-400 text-sm py-8 border-2 border-dashed border-slate-100 rounded-xl">
                                <i className="fas fa-box-open text-2xl mb-2 block"></i>
                                No items added yet
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-slate-100 px-4 sm:px-8 py-4 sm:py-5 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0 bg-slate-50">
                    <button onClick={onClose} className="w-full sm:w-auto px-6 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-bold hover:bg-slate-50 transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto px-6 py-2.5 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 transition-colors shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? <><i className="fas fa-circle-notch fa-spin"></i> Creating...</> : <><i className="fas fa-check"></i> Create Order</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminCreateOrderModal;
